import React, { useCallback, useEffect, useState } from 'react';
import { formatAppDate } from '../../services/appConfigRuntime';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Edit, Refresh } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { usageApi } from '../../services/fileShareApi';

const BYTES_PER_GB = 1024 ** 3;

const bytesToGb = (bytes) => {
  if (bytes == null) return null;
  return Number(bytes) / BYTES_PER_GB;
};

const formatGb = (bytes) => {
  const gb = bytesToGb(bytes) || 0;
  return `${gb.toLocaleString(undefined, { maximumFractionDigits: 2 })} GB`;
};

const limitLabel = (value, suffix = '') =>
  value == null ? 'Not set' : `${Number(value).toLocaleString()}${suffix}`;

const initialForm = {
  translationsLimit: '',
  monthlyGbLimit: '',
  maxFileGb: '',
  unlimitedUntil: '',
};

const FileShareUsageAdmin = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  const loadUsage = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await usageApi.getAdmin();
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || 'Could not load File Share usage.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  const openEdit = (row) => {
    setSelected(row);
    setForm({
      translationsLimit: row.translationsLimit ?? '',
      monthlyGbLimit:
        row.monthlyBytesLimit == null ? '' : bytesToGb(row.monthlyBytesLimit),
      maxFileGb: row.maxFileBytes == null ? '' : bytesToGb(row.maxFileBytes),
      unlimitedUntil: row.unlimitedUntil
        ? new Date(row.unlimitedUntil).toISOString().slice(0, 16)
        : '',
    });
  };

  const closeEdit = () => {
    setSelected(null);
    setForm(initialForm);
  };

  const nullableNumber = (value) =>
    value === '' || value == null ? null : Number(value);

  const gbToBytes = (value) => {
    const parsed = nullableNumber(value);
    return parsed == null ? null : Math.round(parsed * BYTES_PER_GB);
  };

  const saveLimits = async () => {
    const translationsLimit = nullableNumber(form.translationsLimit);
    const monthlyGbLimit = nullableNumber(form.monthlyGbLimit);
    const maxFileGb = nullableNumber(form.maxFileGb);
    const values = [translationsLimit, monthlyGbLimit, maxFileGb];

    if (values.some((value) => value != null && (!Number.isFinite(value) || value < 0))) {
      toast.error('Limits must be zero or a positive number.');
      return;
    }
    if (translationsLimit != null && !Number.isInteger(translationsLimit)) {
      toast.error('Monthly file limit must be a whole number.');
      return;
    }

    setSaving(true);
    try {
      await usageApi.updateLimits(selected.tenantId, {
        translationsLimit,
        monthlyBytesLimit: gbToBytes(form.monthlyGbLimit),
        maxFileBytes: gbToBytes(form.maxFileGb),
        unlimitedUntil: form.unlimitedUntil
          ? new Date(form.unlimitedUntil).toISOString()
          : null,
      });
      toast.success(`Limits updated for ${selected.tenantName}.`);
      closeEdit();
      await loadUsage();
    } catch (err) {
      toast.error(
        err.response?.data?.message || err.message || 'Could not update limits.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: '100%', p: { xs: 1, md: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            File Share Usage
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monthly translation limits and overage. Blank limits mean no cap;
            users can continue after typing approve.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadUsage}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tenant</TableCell>
              <TableCell>Month</TableCell>
              <TableCell align="right">Files used</TableCell>
              <TableCell align="right">Translation overage</TableCell>
              <TableCell align="right">Monthly file limit</TableCell>
              <TableCell align="right">GB used</TableCell>
              <TableCell align="right">GB overage</TableCell>
              <TableCell align="right">Monthly GB limit</TableCell>
              <TableCell align="right">Max GB / file</TableCell>
              <TableCell>Override</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                  No tenants found.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.tenantId} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {row.tenantName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Tenant {row.tenantId}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.yearMonth}</TableCell>
                  <TableCell align="right">{row.translationsUsed || 0}</TableCell>
                  <TableCell align="right">{row.translationsOverage || 0}</TableCell>
                  <TableCell align="right">
                    {limitLabel(row.translationsLimit)}
                  </TableCell>
                  <TableCell align="right">{formatGb(row.bytesUsed)}</TableCell>
                  <TableCell align="right">{formatGb(row.bytesOverage)}</TableCell>
                  <TableCell align="right">
                    {row.monthlyBytesLimit == null
                      ? 'Not set'
                      : formatGb(row.monthlyBytesLimit)}
                  </TableCell>
                  <TableCell align="right">
                    {row.maxFileBytes == null ? 'Not set' : formatGb(row.maxFileBytes)}
                  </TableCell>
                  <TableCell>
                    {row.unlimitedUntil &&
                    new Date(row.unlimitedUntil).getTime() > Date.now() ? (
                      <Chip
                        size="small"
                        color="success"
                        label={`Unlimited until ${formatAppDate(row.unlimitedUntil)}`}
                      />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<Edit />}
                      onClick={() => openEdit(row)}
                    >
                      Edit limits
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(selected)} onClose={closeEdit} fullWidth maxWidth="sm">
        <DialogTitle>Edit limits — {selected?.tenantName}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 0.5 }}>
            Users can continue after typing approve; overage is tracked for
            billing. Leave a field blank for no cap.
          </Alert>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            <TextField
              label="Monthly file limit"
              type="number"
              value={form.translationsLimit}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, translationsLimit: e.target.value }))
              }
              inputProps={{ min: 0, step: 1 }}
              helperText="Maximum translated files per month"
            />
            <TextField
              label="Monthly GB limit"
              type="number"
              value={form.monthlyGbLimit}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, monthlyGbLimit: e.target.value }))
              }
              inputProps={{ min: 0, step: 'any' }}
              helperText="Total source-file GB translated per month"
            />
            <TextField
              label="Max GB per file"
              type="number"
              value={form.maxFileGb}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, maxFileGb: e.target.value }))
              }
              inputProps={{ min: 0, step: 'any' }}
              helperText="A single file cannot exceed this size"
            />
            <TextField
              label="Unlimited until (optional)"
              type="datetime-local"
              value={form.unlimitedUntil}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, unlimitedUntil: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveLimits} disabled={saving}>
            {saving ? 'Saving...' : 'Save limits'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FileShareUsageAdmin;
